/**
 * Role cards (Charter §36, track:cards-1, spec #51346).
 *
 * Pure extraction of the per-role cards from Charter text, shared by the MCP
 * get_role_card tool and Charter Studio so both read the cards the same way.
 * No Firestore, no network. Never falls back to returning the whole Charter.
 */

const SECTION_LINE = "Role Cards (§36)";
const CARD_PREFIX = "Role card: ";
const END_LINE = "End of role cards.";

/** The four roles a service may ask a card for. */
export const ROLE_CARD_ROLES = ["builder", "reviewer", "investigator", "advisor"] as const;

export interface RoleCard {
  role: string;
  preamble: string;
  card: string;
}

interface Section {
  preamble: string;
  cards: { role: string; text: string }[];
}

function parseSection(charterText: string): Section | null {
  const lines = charterText.split(/\r?\n/);
  const start = lines.findIndex((l) => l.trim() === SECTION_LINE);
  if (start === -1) return null;

  const preambleLines: string[] = [];
  const cards: { role: string; lines: string[] }[] = [];
  let current: { role: string; lines: string[] } | null = null;
  let ended = false;

  for (let i = start; i < lines.length && !ended; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith(CARD_PREFIX)) {
      current = { role: trimmed.slice(CARD_PREFIX.length).trim().toLowerCase(), lines: [lines[i]] };
      cards.push(current);
    } else if (trimmed === END_LINE) {
      ended = true;
    } else if (current) {
      current.lines.push(lines[i]);
    } else {
      preambleLines.push(lines[i]);
    }
  }

  return {
    preamble: preambleLines.join("\n").trim(),
    cards: cards.map((c) => ({ role: c.role, text: c.lines.join("\n").trim() })),
  };
}

export function listRoleCards(charterText: string): string[] {
  return parseSection(charterText)?.cards.map((c) => c.role) ?? [];
}

export function extractRoleCard(charterText: string, role: string): RoleCard | null {
  const wanted = role.trim().toLowerCase();
  const section = parseSection(charterText);
  const found = section?.cards.find((c) => c.role === wanted);
  if (!section || !found) return null;
  return { role: found.role, preamble: section.preamble, card: found.text };
}

/** What get_role_card shows for a role, minus the version line. Null if absent. */
export function renderRoleCard(charterText: string, role: string): string | null {
  const r = extractRoleCard(charterText, role);
  return r ? `${r.preamble}\n\n${r.card}` : null;
}
